import React, { useState, useCallback } from 'react';
import ScreenshotUploadModal from './components/ScreenshotUploadModal';
import CalendarPermissionModal from './components/CalendarPermissionModal';
import BottomNav from './components/BottomNav';
import CalendarScreen from './components/CalendarScreen';
import useEventForm from './hooks/useEventForm';
import useSwipe from './hooks/useSwipe';
import useCalendarSync from './hooks/useCalendarSync';
import useNavigation from './hooks/useNavigation';
import useEventActions from './hooks/useEventActions';
import {
  filterEventsForDate,
  sortEventsByTime,
  sortEventsByPriorityThenTime,
} from './utils/eventUtils';
import AddScreen from './screens/AddScreen';
import DetailScreen from './screens/DetailScreen';
import EditScreen from './screens/EditScreen';
import SearchScreen from './screens/SearchScreen';
import SettingsScreen from './screens/SettingsScreen';

export default function CalendarWireframes() {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [screenshotImageUrl, setScreenshotImageUrl] = useState(null);
  const [sortOrder, setSortOrder] = useState('Time');
  const [selectedTags, setSelectedTags] = useState([]);

  const addForm = useEventForm();
  const editForm = useEventForm();

  const nav = useNavigation();
  const {
    currentScreen, setCurrentScreen,
    selectedEvent, setSelectedEvent,
    selectedDate, setSelectedDate,
    currentDay, currentMonth, currentYear,
    lastScreen, setLastScreen,
    cameFromNav, setCameFromNav,
    viewMonth, setViewMonth, viewYear, setViewYear,
    searchQuery, setSearchQuery,
    todayDay, todayMonth,
    navigateDay, goToToday,
  } = nav;

  const calSync = useCalendarSync();
  const {
    googleCalConnected, googleAccessToken, googleEvents, setGoogleEvents,
    allEvents, isSyncing, syncError, setSyncError,
    showCalPermissionModal, setShowCalPermissionModal,
    pendingCalendar, setPendingCalendar,
    fetchAndSyncGoogleEvents, disconnectGoogle, GOOGLE_CLIENT_ID,
  } = calSync;

  const resetAddForm = useCallback(() => {
    addForm.reset();
    setSelectedTags([]);
  }, [addForm]);

  const { handleSaveNewEvent, handleSaveEditedEvent } = useEventActions({
    addForm, editForm, selectedTags, selectedEvent,
    googleCalConnected, googleAccessToken, googleEvents, setGoogleEvents,
    setSyncError, lastScreen, resetAddForm, setCurrentScreen, setSelectedEvent,
  });

  const swipeHandlers = useSwipe(
    () => navigateDay(1),
    () => navigateDay(-1)
  );

  const toggleTag = (tag) => {
    setSelectedTags(prev => prev.includes(tag) ? [] : [tag]);
  };

  const openEventDetail = (event) => {
    setSelectedEvent(event);
    setSelectedTags(event.tags || []);
    editForm.populate(event);
    setLastScreen('home');
    setCurrentScreen('detail');
  };

  const openEventDetailFromSearch = (event) => {
    setSelectedEvent(event);
    setSelectedTags(event.tags || []);
    nav.setCurrentDay(event.day);
    nav.setCurrentMonth(event.month);
    setSearchQuery("");
    editForm.populate(event);
    setLastScreen("search");
    setCurrentScreen("detail");
  };

  const openEditEvent = () => {
    if (!selectedEvent) return;
    editForm.populate(selectedEvent);
    setSelectedTags(selectedEvent.tags || []);
    setLastScreen('detail');
    setCurrentScreen('edit');
  };

  const currentDateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(currentDay).padStart(2, '0')}`;

  const handleManualAdd = (fromScreen) => {
    setLastScreen(fromScreen);
    setCameFromNav(false);
    resetAddForm();
    addForm.setField('date', currentDateStr);
    setCurrentScreen('add');
  };

  const handleScreenshotContinue = (imageUrl) => {
    setShowUploadModal(false);
    setScreenshotImageUrl(imageUrl);
    setCameFromNav(false);
    setLastScreen(currentScreen === 'month' ? 'month' : 'home');
    resetAddForm();
    addForm.setField('date', currentDateStr);
    setCurrentScreen('add');
  };

  const handleScreenshotParsed = useCallback((parsedFormData) => {
    if (parsedFormData.title) addForm.setField('title', parsedFormData.title);
    if (parsedFormData.date) addForm.setField('date', parsedFormData.date);
    if (parsedFormData.startTime) addForm.setField('startTime', parsedFormData.startTime);
    if (parsedFormData.endTime) addForm.setField('endTime', parsedFormData.endTime);
    if (parsedFormData.location) addForm.setField('location', parsedFormData.location);
    setScreenshotImageUrl(null);
  }, [addForm]);

  const rawEvents = sortEventsByTime(filterEventsForDate(allEvents, currentDay, currentMonth, currentYear));
  const events = sortOrder === "Priority" ? sortEventsByPriorityThenTime(rawEvents) : rawEvents;

  return (
    <div className="w-full h-screen bg-white">
      <div className="h-full overflow-hidden flex flex-col">
        <div className="flex-1 overflow-hidden relative">
          {(currentScreen === 'home' || currentScreen === 'month') && (
            <CalendarScreen
              currentScreen={currentScreen}
              onScreenshot={() => setShowUploadModal(true)}
              onManualAdd={() => handleManualAdd(currentScreen)}
              currentDay={currentDay} currentMonth={currentMonth} currentYear={currentYear}
              todayDay={todayDay} todayMonth={todayMonth}
              navigateDay={navigateDay} events={events} setSortOrder={setSortOrder}
              isSyncing={isSyncing} syncError={syncError} setSyncError={setSyncError}
              openEventDetail={openEventDetail} setCurrentScreen={setCurrentScreen}
              swipeHandlers={swipeHandlers}
              viewMonth={viewMonth} viewYear={viewYear} setViewMonth={setViewMonth} setViewYear={setViewYear}
              selectedDate={selectedDate} setSelectedDate={setSelectedDate}
              allEvents={allEvents}
              todayYear={new Date().getFullYear()}
            />
          )}
          {currentScreen === 'add' && (
            <AddScreen
              addForm={addForm} selectedTags={selectedTags} toggleTag={toggleTag}
              cameFromNav={cameFromNav} lastScreen={lastScreen}
              setCurrentScreen={setCurrentScreen} setShowUploadModal={setShowUploadModal}
              handleSaveNewEvent={handleSaveNewEvent}
              screenshotImageUrl={screenshotImageUrl}
              onScreenshotParsed={handleScreenshotParsed}
            />
          )}
          {currentScreen === 'detail' && (
            <DetailScreen
              selectedEvent={selectedEvent} lastScreen={lastScreen}
              setCurrentScreen={setCurrentScreen}
              onEdit={openEditEvent}
            />
          )}
          {currentScreen === 'edit' && (
            <EditScreen
              editForm={editForm} selectedTags={selectedTags} toggleTag={toggleTag}
              setCurrentScreen={setCurrentScreen}
              handleSaveEditedEvent={handleSaveEditedEvent}
            />
          )}
          {currentScreen === 'manual' && (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500 text-lg">Manual Entry Screen (Coming Soon)</div>
            </div>
          )}
          {currentScreen === 'search' && (
            <SearchScreen
              searchQuery={searchQuery} setSearchQuery={setSearchQuery}
              allEvents={allEvents} openEventDetailFromSearch={openEventDetailFromSearch}
              setCurrentScreen={setCurrentScreen}
            />
          )}
          {currentScreen === 'settings' && (
            <SettingsScreen
              googleCalConnected={googleCalConnected} googleAccessToken={googleAccessToken}
              isSyncing={isSyncing} syncError={syncError} setSyncError={setSyncError}
              fetchAndSyncGoogleEvents={fetchAndSyncGoogleEvents} disconnectGoogle={disconnectGoogle}
              GOOGLE_CLIENT_ID={GOOGLE_CLIENT_ID} setCurrentScreen={setCurrentScreen}
              setPendingCalendar={setPendingCalendar} setShowCalPermissionModal={setShowCalPermissionModal}
            />
          )}

          <ScreenshotUploadModal
            isOpen={showUploadModal}
            onClose={() => setShowUploadModal(false)}
            onContinue={handleScreenshotContinue}
          />
          <CalendarPermissionModal
            show={showCalPermissionModal}
            pendingCalendar={pendingCalendar}
            onClose={() => { setShowCalPermissionModal(false); setPendingCalendar(null); }}
          />
        </div>

        <BottomNav
          currentScreen={currentScreen} todayDay={todayDay} todayMonth={todayMonth}
          goToToday={goToToday} setCurrentScreen={setCurrentScreen}
        />
      </div>
    </div>
  );
}
